"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { ApiWaveCreditScope } from "@/generated/models/ApiWaveCreditScope";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveApprovalTiming, { CreateWaveApprovalTimingMode, getCreateWaveApprovalTimingMode } from "./CreateWaveApprovalTiming";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import CreateWaveVotingThresholdTime from "./CreateWaveVotingThresholdTime";
import MemeCardSetPicker from "./MemeCardSetPicker";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.CardSetTdh]: 3,
  [ApiWaveCreditType.Xtdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const APPROVAL_THRESHOLD_TIME_INVALID_ERROR = "Enter a whole number greater than 0, or leave blank for immediate approval.";
const APPROVAL_THRESHOLD_TIME_DURATION_ERROR = "This time is longer than the wave duration. Choose a shorter time, extend the wave end date, or clear the end date.";
const APPROVAL_TIMING_CONFLICT_ERROR = "Choose either minimum time above threshold or time-weighted voting.";
const DEFAULT_APPROVAL_THRESHOLD_TIME_MS = 60 * 1000;
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
const VOTING_OPTIONS_GRID_CLASSES = "tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4 [&>div]:tw-gap-x-2 [&>div]:tw-px-3 [&>div]:tw-py-3";
const CREDIT_SCOPE_OPTIONS: readonly {
  readonly scope: ApiWaveCreditScope;
  readonly label: string;
  readonly description: string;
}[] = [{
  scope: ApiWaveCreditScope.Wave,
  label: "Whole wave",
  description: "Each identity has one voting budget across the wave."
}, {
  scope: ApiWaveCreditScope.Drop,
  label: "Each drop",
  description: "Voting power applies separately to every drop."
}];
const getCreateWaveVotingLabel = (votingType: ApiWaveCreditType): string => {
  if (votingType === ApiWaveCreditType.CardSetTdh) {
    return "Memes TDH";
  }
  return WAVE_VOTING_LABELS[votingType];
};
const getApprovalThresholdTimeErrorMessage = (errors: CREATE_WAVE_VALIDATION_ERROR[]): string | undefined => {
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_EXCEEDS_WAVE_DURATION)) {
    return APPROVAL_THRESHOLD_TIME_DURATION_ERROR;
  }
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_INVALID)) {
    return APPROVAL_THRESHOLD_TIME_INVALID_ERROR;
  }
  return undefined;
};
function CreateWaveCreditScopeSelect(t0) {
  const $ = _c(6);
  const {
    creditScope,
    onCreditScopeChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <legend className="tw-mb-3 tw-block tw-text-sm tw-font-semibold tw-text-iron-100">Voting power scope</legend>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== creditScope || $[2] !== onCreditScopeChange) {
    t2 = CREDIT_SCOPE_OPTIONS.map(option => {
      const selected = creditScope === option.scope;
      return <label key={option.scope} className={`tw-flex tw-cursor-pointer tw-gap-x-3 tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out ${selected ? "tw-border-primary-400 tw-bg-primary-500/5 tw-ring-primary-500/30" : "tw-border-white/5 tw-bg-iron-900 tw-ring-white/5 hover:tw-border-white/10 hover:tw-bg-iron-800 hover:tw-ring-white/10"}`}><input type="radio" name="create-wave-credit-scope" value={option.scope} checked={selected} onChange={() => onCreditScopeChange(option.scope)} className="tw-mt-1 tw-form-radio tw-h-4 tw-w-4 tw-cursor-pointer tw-border tw-border-solid tw-border-iron-650 tw-bg-iron-800 tw-text-primary-400 tw-ring-offset-iron-800 tw-transition tw-duration-300 tw-ease-out focus:tw-ring-2 focus:tw-ring-primary-400" /><span className="tw-min-w-0"><span className={`tw-block tw-text-sm tw-font-semibold ${selected ? "tw-text-primary-400" : "tw-text-iron-200"}`}>{option.label}</span><span className="tw-mt-1 tw-block tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-400">{option.description}</span></span></label>;
    });
    $[1] = creditScope;
    $[2] = onCreditScopeChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <fieldset className="tw-mt-6 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-700 tw-px-0 tw-pb-0 tw-pt-6">{t1}<div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">{t2}</div></fieldset>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export default function CreateWaveVoting(t0) {
  const $ = _c(82);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    creditNfts,
    creditScope,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    allowNegativeVotes,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    approvalThresholdTimeMs,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setCreditNfts,
    onCreditScopeChange,
    onAllowNegativeVotesChange,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    setApprovalThresholdTimeMs,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  const [approvalTimingModeOverride, setApprovalTimingModeOverride] = useState(null);
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  let t3;
  if ($[3] !== errors) {
    t3 = getApprovalThresholdTimeErrorMessage(errors);
    $[3] = errors;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const approvalThresholdTimeErrorMessage = t3;
  const approvalTimingErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_TIMING_OPTIONS_MUTUALLY_EXCLUSIVE) ? APPROVAL_TIMING_CONFLICT_ERROR : undefined;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  let t4;
  if ($[5] !== approvalThresholdTimeMs || $[6] !== timeWeighted) {
    t4 = getCreateWaveApprovalTimingMode({
      thresholdTimeMs: approvalThresholdTimeMs,
      timeWeighted
    });
    $[5] = approvalThresholdTimeMs;
    $[6] = timeWeighted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const inferredApprovalTimingMode = t4;
  const approvalTimingMode = approvalTimingModeOverride ?? inferredApprovalTimingMode;
  let t5;
  if ($[8] !== approvalTimingMode || $[9] !== timeWeighted) {
    t5 = approvalTimingMode === CreateWaveApprovalTimingMode.TIME_WEIGHTED ? {
      ...timeWeighted,
      enabled: true
    } : timeWeighted;
    $[8] = approvalTimingMode;
    $[9] = timeWeighted;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const timeWeightedConfig = t5;
  let t6;
  if ($[11] !== approvalThresholdTimeMs || $[12] !== onTimeWeightedChange || $[13] !== setApprovalThresholdTimeMs || $[14] !== timeWeighted) {
    t6 = mode => {
      setApprovalTimingModeOverride(mode);
      bb21: switch (mode) {
        case CreateWaveApprovalTimingMode.IMMEDIATE:
          {
            setApprovalThresholdTimeMs(null);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: false
            });
            break bb21;
          }
        case CreateWaveApprovalTimingMode.THRESHOLD_TIME:
          {
            setApprovalThresholdTimeMs(approvalThresholdTimeMs !== null && Number.isFinite(approvalThresholdTimeMs) && approvalThresholdTimeMs > 0 ? approvalThresholdTimeMs : DEFAULT_APPROVAL_THRESHOLD_TIME_MS);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: false
            });
            break bb21;
          }
        case CreateWaveApprovalTimingMode.TIME_WEIGHTED:
          {
            setApprovalThresholdTimeMs(null);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: true
            });
          }
      }
    };
    $[11] = approvalThresholdTimeMs;
    $[12] = onTimeWeightedChange;
    $[13] = setApprovalThresholdTimeMs;
    $[14] = timeWeighted;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onApprovalTimingModeChange = t6;
  let t7;
  if ($[16] !== setApprovalThresholdTimeMs) {
    t7 = value => {
      setApprovalTimingModeOverride(CreateWaveApprovalTimingMode.THRESHOLD_TIME);
      setApprovalThresholdTimeMs(value);
    };
    $[16] = setApprovalThresholdTimeMs;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const onApprovalThresholdTimeChange = t7;
  const t8 = TITLES[waveType];
  let t9;
  if ($[18] !== t8) {
    t9 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t8}</p>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== onTypeChange || $[21] !== selectedType) {
    t10 = (Object.keys(VOTING_TYPES_ORDER) as ApiWaveCreditType[]).filter(_temp).map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{getCreateWaveVotingLabel(votingType_0)}</span></CommonBorderedRadioButton>);
    $[20] = onTypeChange;
    $[21] = selectedType;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== category || $[24] !== errors || $[25] !== profileId || $[26] !== selectedType || $[27] !== setCategory || $[28] !== setProfileId) {
    t11 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[23] = category;
    $[24] = errors;
    $[25] = profileId;
    $[26] = selectedType;
    $[27] = setCategory;
    $[28] = setProfileId;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== creditNfts || $[31] !== errors || $[32] !== isMemeCountError || $[33] !== isMemeCountLoading || $[34] !== memeCount || $[35] !== selectedType || $[36] !== setCreditNfts) {
    t12 = selectedType === ApiWaveCreditType.CardSetTdh && <MemeCardSetPicker creditNfts={creditNfts} memeCount={memeCount} isMemeCountLoading={isMemeCountLoading} isMemeCountError={isMemeCountError} errors={errors} onCreditNftsChange={setCreditNfts} />;
    $[30] = creditNfts;
    $[31] = errors;
    $[32] = isMemeCountError;
    $[33] = isMemeCountLoading;
    $[34] = memeCount;
    $[35] = selectedType;
    $[36] = setCreditNfts;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12) {
    t13 = <div className={VOTING_OPTIONS_GRID_CLASSES}>{t10}{t11}{t12}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== creditScope || $[43] !== onCreditScopeChange) {
    t14 = <CreateWaveCreditScopeSelect creditScope={creditScope} onCreditScopeChange={onCreditScopeChange} />;
    $[42] = creditScope;
    $[43] = onCreditScopeChange;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== approvalThreshold || $[46] !== approvalThresholdError || $[47] !== errors || $[48] !== maxVotesPerIdentityPerDrop || $[49] !== setApprovalThreshold || $[50] !== setMaxVotesPerIdentityPerDrop || $[51] !== showVotingSettings || $[52] !== waveType) {
    t15 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} />}</div>;
    $[45] = approvalThreshold;
    $[46] = approvalThresholdError;
    $[47] = errors;
    $[48] = maxVotesPerIdentityPerDrop;
    $[49] = setApprovalThreshold;
    $[50] = setMaxVotesPerIdentityPerDrop;
    $[51] = showVotingSettings;
    $[52] = waveType;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== approvalThresholdTimeErrorMessage || $[55] !== approvalThresholdTimeMs || $[56] !== approvalTimingErrorMessage || $[57] !== approvalTimingMode || $[58] !== onApprovalThresholdTimeChange || $[59] !== onApprovalTimingModeChange || $[60] !== onTimeWeightedChange || $[61] !== timeWeightedConfig || $[62] !== timeWeightedErrorMessage || $[63] !== waveType) {
    t16 = waveType === ApiWaveType.Approve && <><CreateWaveApprovalTiming selectedMode={approvalTimingMode} errorMessage={approvalTimingErrorMessage} onModeChange={onApprovalTimingModeChange} />{approvalTimingMode === CreateWaveApprovalTimingMode.THRESHOLD_TIME && <div className="tw-mt-3" data-testid="approval-timing-detail"><CreateWaveVotingThresholdTime thresholdTimeMs={approvalThresholdTimeMs} errorMessage={approvalThresholdTimeErrorMessage} setThresholdTimeMs={onApprovalThresholdTimeChange} /></div>}{approvalTimingMode === CreateWaveApprovalTimingMode.TIME_WEIGHTED && <div className="tw-mt-3" data-testid="approval-timing-detail"><TimeWeightedVoting config={timeWeightedConfig} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} showToggle={false} /></div>}</>;
    $[54] = approvalThresholdTimeErrorMessage;
    $[55] = approvalThresholdTimeMs;
    $[56] = approvalTimingErrorMessage;
    $[57] = approvalTimingMode;
    $[58] = onApprovalThresholdTimeChange;
    $[59] = onApprovalTimingModeChange;
    $[60] = onTimeWeightedChange;
    $[61] = timeWeightedConfig;
    $[62] = timeWeightedErrorMessage;
    $[63] = waveType;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== allowNegativeVotes || $[66] !== onAllowNegativeVotesChange || $[67] !== waveType) {
    t17 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={onAllowNegativeVotesChange} isDisabled={false} />;
    $[65] = allowNegativeVotes;
    $[66] = onAllowNegativeVotesChange;
    $[67] = waveType;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== onTimeWeightedChange || $[70] !== timeWeighted || $[71] !== timeWeightedErrorMessage || $[72] !== waveType) {
    t18 = waveType === ApiWaveType.Rank && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[69] = onTimeWeightedChange;
    $[70] = timeWeighted;
    $[71] = timeWeightedErrorMessage;
    $[72] = waveType;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  let t19;
  if ($[74] !== t13 || $[75] !== t14 || $[76] !== t15 || $[77] !== t16 || $[78] !== t17 || $[79] !== t18 || $[80] !== t9) {
    t19 = <div>{t9}{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[74] = t13;
    $[75] = t14;
    $[76] = t15;
    $[77] = t16;
    $[78] = t17;
    $[79] = t18;
    $[80] = t9;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  return t19;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}